import React, { ChangeEvent, useMemo, useState } from 'react'
import { Table } from 'react-bootstrap'
import { useLocation } from 'react-router-dom'
import { PageEventArgs } from '@syncfusion/ej2-react-grids'
import SearchWithFilter, {
  FilterSelection,
} from '@components/common/SearchWithFilter/SearchWithFilter.component'
import PaginationContainer from '@components/common/PaginationContainer/PaginationContainer.component'
import { SortDirection } from '@constants/Common/sort-direction.constant'
import {
  DASHBOARD_DETAIL_MAX_ROWS_WITHOUT_CONTROLS,
  isDashboardDetailRoute,
} from '@constants/Dashboard/dashboardDetailContext.util'
import { FILTER_SHOW_ALL } from '@constants/Notifications'
import DashboardPatientNameLink from '@components/pages/Dashboard/Shared/DashboardPatientNameLink.component'
import { toDashboardSentenceCase } from '@utils/dashboardText.util'
import { renderDashboardTruncatedText } from '@utils/dashboardTruncatedText.util'
import {
  isDashboardPatientColumn,
  resolvePatientIdFromDashboardRow,
} from '@utils/dashboardPatientLink.util'
import DashboardSectionEmptyState from '@components/pages/Dashboard/DashboardSectionEmptyState.component'
import './DashboardSharedTable.style.css'

function isDashboardActionsColumn(col: Column): boolean {
  const key = col.key.toLowerCase()
  const label = col.label.trim().toLowerCase()
  return key.includes('action') || label === 'action' || label === 'actions'
}

function mergeColumnClassName(
  col: Column,
  slot: 'header' | 'cell',
): string | undefined {
  const base = slot === 'header' ? col.headerClassName : col.className
  if (!isDashboardActionsColumn(col)) return base
  return [base, 'dst-actions-col'].filter(Boolean).join(' ')
}

type Column = {
  key: string
  label: string
  formatter?: (value: any, row: any) => React.ReactNode
  style?: React.CSSProperties
  sortable?: boolean
  headerClassName?: string
  className?: string
  /** When true, patient name cells link to Patient Profile. Auto-detected for patient columns. */
  linkToPatientProfile?: boolean
  /** When false, cell text is not truncated with ellipsis. Defaults to true. */
  truncate?: boolean
}

function shouldTruncateColumn(col: Column): boolean {
  if (col.truncate === false) return false
  if (isDashboardActionsColumn(col)) return false
  if (col.formatter) return false
  return true
}

function renderCellContent(
  col: Column,
  cellValue: unknown,
  row: Record<string, unknown>,
): React.ReactNode {
  if (col.formatter) {
    return col.formatter(cellValue, row)
  }

  const text = String(cellValue ?? '').trim()
  if (!text) {
    return text
  }

  if (
    isDashboardPatientColumn(col) &&
    !/^\d+$/.test(text) &&
    !/^\$/.test(text) &&
    !/^\d+(\.\d+)?%$/.test(text)
  ) {
    const patientId = resolvePatientIdFromDashboardRow(row, text)
    if (patientId) {
      return <DashboardPatientNameLink patientId={patientId} name={text} />
    }
  }

  if (shouldTruncateColumn(col)) {
    return renderDashboardTruncatedText(text)
  }

  return text
}

type SortConfig = {
  key: string | null
  direction: SortDirection
}

type DashboardSharedTableProps = {
  columns: Column[]
  data: any[]
  additionalRows?: (row: any) => React.ReactNode
  className?: string
  rowClassName?: (row: any) => string
  onRowClick?: (row: any) => void
  highlightSelectedRow?: boolean
  pageSize?: number
  showPagination?: boolean
  useBackendPagination?: boolean
  useBackendSorting?: boolean
  onSortChange?: (sort: {
    key: string | null
    direction: SortDirection
  }) => void
  totalCount?: number
  currentPage?: number
  onPageChange?: (page: number) => void
  onPageSizeChange?: (pageSize: number) => void
  title?: string
  filters?: Record<string, string[]>
  onFilterChange?: (selectedFilters: FilterSelection) => void
  filterLabels?: Record<string, string>
  searchIcon?: string
  filterIcon?: string
  placeholder?: string
  searchClassName?: string
  maxHeight?: string
  /** Use normal table layout without a scrollable tbody (e.g. short overview lists). */
  disableBodyScroll?: boolean
  footer?: () => React.ReactNode
  enableAutoFilters?: boolean
  maxAutoFilterOptions?: number
  showSearchFilter?: boolean
  /** When set with `onSearchChange`, controls the header search (e.g. sync chart + table). */
  searchQuery?: string
  onSearchChange?: (event: ChangeEvent<HTMLInputElement>) => void
  /** When true, suppress the empty state so loading does not flash "No data". */
  loading?: boolean
}

function DashboardSharedTable({
  columns,
  data,
  additionalRows,
  className,
  rowClassName,
  onRowClick,
  highlightSelectedRow = true,
  pageSize: initialPageSize = 10,
  showPagination = true,
  useBackendPagination = false,
  useBackendSorting = false,
  onSortChange,
  totalCount,
  currentPage: externalCurrentPage,
  onPageChange: externalOnPageChange,
  onPageSizeChange: externalOnPageSizeChange,
  title,
  filters,
  onFilterChange,
  filterLabels,
  searchIcon = 'mgc mgc_search_2_line',
  filterIcon = 'mgc mgc_filter_2_line',
  placeholder,
  searchClassName,
  maxHeight = '500px',
  disableBodyScroll = false,
  footer,
  enableAutoFilters = true,
  maxAutoFilterOptions = 20,
  showSearchFilter = true,
  searchQuery: searchQueryProp,
  onSearchChange,
  loading = false,
}: DashboardSharedTableProps): React.ReactElement {
  const location = useLocation()
  const isDetailPage = isDashboardDetailRoute(location.pathname)
  const sourceRowCount = useBackendPagination
    ? (totalCount ?? data.length)
    : data.length
  const hideControlsForFewRows =
    !useBackendPagination &&
    isDetailPage &&
    sourceRowCount <= DASHBOARD_DETAIL_MAX_ROWS_WITHOUT_CONTROLS
  const effectiveShowPagination = showPagination && !hideControlsForFewRows
  const effectiveShowSearchFilter = showSearchFilter && !hideControlsForFewRows

  const [internalCurrentPage, setInternalCurrentPage] = useState<number>(1)
  const [internalPageSize, setInternalPageSize] =
    useState<number>(initialPageSize)
  const [selectedRowKey, setSelectedRowKey] = useState<string | null>(null)
  const [internalSearchQuery, setInternalSearchQuery] = useState<string>('')
  const searchQuery =
    searchQueryProp !== undefined ? searchQueryProp : internalSearchQuery
  const [selectedFilters, setSelectedFilters] = useState<FilterSelection>({})
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    key: null,
    direction: SortDirection.ASC,
  })

  const autoFilters = useMemo(() => {
    if (!enableAutoFilters || !Array.isArray(data) || data.length === 0)
      return {}
    const next: Record<string, string[]> = {}

    columns.forEach((col) => {
      const key = col.key
      const keyLower = key.toLowerCase()
      // Skip action-like / free-text columns to keep filter menus usable.
      if (
        keyLower.includes('action') ||
        keyLower.includes('note') ||
        keyLower.includes('message')
      ) {
        return
      }

      const values = new Set<string>()
      for (const row of data) {
        const v = (row as Record<string, unknown>)[key]
        if (v === null || v === undefined) continue
        const text = String(v).trim()
        if (!text) continue
        values.add(text)
        if (values.size > maxAutoFilterOptions) break
      }

      // Only add filters when the field has useful variety.
      if (values.size >= 2 && values.size <= maxAutoFilterOptions) {
        next[key] = [FILTER_SHOW_ALL, ...Array.from(values).sort()]
      }
    })

    return next
  }, [columns, data, enableAutoFilters, maxAutoFilterOptions])

  const effectiveFilters =
    filters && Object.keys(filters).length > 0 ? filters : autoFilters

  const getRowKey = (row: unknown, index: number): string => {
    const rowRecord = row as Record<string, unknown>
    if (rowRecord.id && typeof rowRecord.id === 'string') return rowRecord.id
    return `row-${index}`
  }

  const categoryToKeyMap = useMemo(() => {
    const map: Record<string, string> = {}
    columns.forEach((col) => {
      map[col.key.toLowerCase()] = col.key
      map[col.label.toLowerCase()] = col.key
      map[col.label.toLowerCase().replace(/\s+/g, '')] = col.key
      map[col.label.toLowerCase().replace(/\s+/g, '_')] = col.key
    })
    return map
  }, [columns])

  const filteredData = useMemo(() => {
    if (useBackendPagination) return data

    const filterCategories = Object.entries(selectedFilters).filter(
      ([, values]) =>
        Array.isArray(values) &&
        values.length > 0 &&
        !values.includes(FILTER_SHOW_ALL),
    )

    const query = searchQuery.trim().toLowerCase()

    if (filterCategories.length === 0 && !query) return data

    return data.filter((row) => {
      const rowRecord = row as Record<string, unknown>

      const passesFilters = filterCategories.every(([category, values]) => {
        const normalizedCategory = category.toLowerCase()
        const mappedKey =
          categoryToKeyMap[normalizedCategory] ||
          categoryToKeyMap[normalizedCategory.replace(/\s+/g, '')] ||
          categoryToKeyMap[normalizedCategory.replace(/\s+/g, '_')]
        const candidateValue =
          (mappedKey ? rowRecord[mappedKey] : undefined) ??
          rowRecord[category] ??
          rowRecord[normalizedCategory]

        if (candidateValue == null) return false

        const text = String(candidateValue).toLowerCase()
        return values.some((v) => text === String(v).toLowerCase())
      })

      if (!passesFilters) return false
      if (!query) return true

      return columns.some((col) => {
        const value = rowRecord[col.key]
        return value !== null && value !== undefined
          ? String(value).toLowerCase().includes(query)
          : false
      })
    })
  }, [
    categoryToKeyMap,
    columns,
    data,
    searchQuery,
    selectedFilters,
    useBackendPagination,
  ])

  const currentPage = useBackendPagination
    ? (externalCurrentPage ?? 1)
    : internalCurrentPage
  const pageSize = useBackendPagination ? initialPageSize : internalPageSize

  const handleSort = (key: string): void => {
    if (sortConfig.key === key && sortConfig.direction === SortDirection.DESC) {
      const cleared: SortConfig = { key: null, direction: SortDirection.ASC }
      if (useBackendSorting && onSortChange) onSortChange(cleared)
      setSortConfig(cleared)
      return
    }
    const direction: SortDirection =
      sortConfig.key === key ? SortDirection.DESC : SortDirection.ASC
    const next: SortConfig = { key, direction }
    if (useBackendSorting && onSortChange) onSortChange(next)
    setSortConfig(next)
  }

  const sortedData = useMemo(() => {
    if (useBackendSorting) return [...filteredData]
    if (!sortConfig.key) return [...filteredData]
    return [...filteredData].sort((a, b) => {
      const aValue = a[sortConfig.key!]
      const bValue = b[sortConfig.key!]
      if (aValue == null) return 1
      if (bValue == null) return -1
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortConfig.direction === SortDirection.ASC
          ? aValue - bValue
          : bValue - aValue
      }
      return sortConfig.direction === SortDirection.ASC
        ? String(aValue).localeCompare(String(bValue))
        : String(bValue).localeCompare(String(aValue))
    })
  }, [filteredData, sortConfig, useBackendSorting])

  const totalRecordsCount = useBackendPagination
    ? (totalCount ?? 0)
    : sortedData.length
  const totalPages = Math.ceil(totalRecordsCount / pageSize)

  const paginatedData = useMemo(() => {
    if (!effectiveShowPagination) return sortedData
    if (useBackendPagination) return sortedData
    const start = (currentPage - 1) * pageSize
    return sortedData.slice(start, start + pageSize)
  }, [
    sortedData,
    currentPage,
    pageSize,
    effectiveShowPagination,
    useBackendPagination,
  ])

  const handlePageChange = (args: PageEventArgs): void => {
    const page = parseInt(args.currentPage as string, 10) || 1
    if (page <= 0 || page > totalPages) return
    if (useBackendPagination && externalOnPageChange) externalOnPageChange(page)
    else setInternalCurrentPage(page)
  }

  const handlePageSizeChange = (newPageSize: number): void => {
    if (useBackendPagination && externalOnPageSizeChange) {
      externalOnPageSizeChange(newPageSize)
      return
    }
    setInternalPageSize(newPageSize)
    setInternalCurrentPage(1)
  }

  const showHeader = title || effectiveShowSearchFilter
  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>): void => {
    if (onSearchChange) {
      onSearchChange(event)
      return
    }
    setInternalSearchQuery(event.target.value)
  }
  const handleFilterChange = (next: FilterSelection): void => {
    setSelectedFilters(next)
    onFilterChange?.(next)
  }

  const showEmptyState = !loading && paginatedData.length === 0

  return (
    <div
      className={[
        'dashboard-shared-table',
        disableBodyScroll ? 'dashboard-shared-table--no-body-scroll' : '',
        showEmptyState ? 'dashboard-shared-table--empty' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {showHeader && (
        <div className="dst-header">
          {title && (
            <h3 className="dst-title">{toDashboardSentenceCase(title)}</h3>
          )}
          {effectiveShowSearchFilter && (
            <div className="dst-search-slot">
              <SearchWithFilter
                searchIcon={searchIcon}
                filterIcon={filterIcon}
                placeholder={placeholder}
                searchQuery={searchQuery}
                onChange={handleSearchChange}
                className={searchClassName}
                filters={effectiveFilters}
                onFilterChange={handleFilterChange}
                selectedFilters={selectedFilters}
                filterLabels={filterLabels}
              />
            </div>
          )}
        </div>
      )}

      {loading && paginatedData.length === 0 ? null : showEmptyState ? (
        <DashboardSectionEmptyState className="dashboard-section-empty--embed" />
      ) : (
        <>
          <div className="dst-wrapper">
            <Table
              hover
              responsive
              className={`dst-table mb-0 ${className || ''}`}
            >
              <thead className="dst-thead">
                <tr>
                  {columns.map((col) => (
                    <th
                      key={col.key}
                      style={{
                        ...col.style,
                        cursor: col.sortable ? 'pointer' : 'default',
                      }}
                      className={mergeColumnClassName(col, 'header') || ''}
                      onClick={() => col.sortable && handleSort(col.key)}
                    >
                      <span className="dst-th-content">
                        <span className="dst-th-label">
                          {toDashboardSentenceCase(col.label)}
                        </span>
                        {col.sortable && (
                          <span className="dst-sort-arrows" aria-hidden="true">
                            <span
                              className={
                                'dst-sort-tri dst-sort-tri--up' +
                                (sortConfig.key === col.key &&
                                sortConfig.direction === SortDirection.ASC
                                  ? ' dst-sort-tri--active'
                                  : '')
                              }
                            />
                            <span
                              className={
                                'dst-sort-tri dst-sort-tri--down' +
                                (sortConfig.key === col.key &&
                                sortConfig.direction === SortDirection.DESC
                                  ? ' dst-sort-tri--active'
                                  : '')
                              }
                            />
                          </span>
                        )}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody
                className="dst-tbody"
                style={disableBodyScroll ? undefined : { maxHeight }}
              >
                {paginatedData.map((row, index) => {
                  const rowKey = getRowKey(row, index)
                  const rowRecord = row as Record<string, unknown>
                  const isSelected =
                    highlightSelectedRow && selectedRowKey === rowKey
                  const customRowClass = rowClassName ? rowClassName(row) : ''
                  return (
                    <React.Fragment key={rowKey}>
                      <tr
                        className={`${isSelected ? 'dst-row-selected' : ''} ${customRowClass}`.trim()}
                        onClick={() => {
                          if (!onRowClick) return
                          if (highlightSelectedRow) setSelectedRowKey(rowKey)
                          onRowClick(row)
                        }}
                        style={{ cursor: onRowClick ? 'pointer' : 'default' }}
                      >
                        {columns.map((col) => {
                          const cellValue = rowRecord[col.key]
                          return (
                            <td
                              key={col.key}
                              style={col.style}
                              className={mergeColumnClassName(col, 'cell')}
                            >
                              {renderCellContent(col, cellValue, rowRecord)}
                            </td>
                          )
                        })}
                      </tr>
                      {additionalRows && additionalRows(row)}
                    </React.Fragment>
                  )
                })}
                {footer && footer()}
              </tbody>
            </Table>
          </div>

          {effectiveShowPagination && (
            <PaginationContainer
              totalRecordsCount={totalRecordsCount}
              pageSize={pageSize}
              currentPage={currentPage}
              pageSizeOptions={[10, 25, 50, 100]}
              onPageSizeChange={handlePageSizeChange}
              onPageChange={handlePageChange}
            />
          )}
        </>
      )}
    </div>
  )
}

export default React.memo(DashboardSharedTable) as typeof DashboardSharedTable
